import {
  HandThumbDownIcon,
  HandThumbUpIcon,
} from "@heroicons/react/24/outline";
import { animated, useSpring } from "@react-spring/web";
import React, { ComponentProps, useState } from "react";
import { twMerge } from "tailwind-merge";

import { useLang } from "@/hooks/use-lang";

import { Feedback, useAddFeedback } from "../../services/add-feedback";

type FeedbackButtonsProps = {
  messageId: string;
  value: Feedback;
};

const FeedbackButtons = ({ messageId, value }: FeedbackButtonsProps) => {
  const { t } = useLang();
  const [feedback, setFeedback] = useState<Feedback | null>(value);
  const { mutate: addFeedback } = useAddFeedback();

  const handleFeedback = (selectedFeedback: Feedback) => {
    if (!messageId) return;
    setFeedback(selectedFeedback);
    addFeedback({ messageId, feedback: selectedFeedback });
  };

  return (
    <div className="flex items-center gap-2 text-zinc-500 sm:pl-16">
      <AnimatedButton
        active={feedback === "like"}
        icon={
          <HandThumbUpIcon
            width="100%"
            height="100%"
            className={twMerge(
              feedback === "like" && "stroke-2 text-orange-400"
            )}
          />
        }
        text={t.winwingpt.like}
        onClick={() => handleFeedback("like")}
        aria-pressed={feedback === "like"}
      />
      <AnimatedButton
        active={feedback === "dislike"}
        icon={
          <HandThumbDownIcon
            width="100%"
            height="100%"
            className={twMerge(
              "group-hover:stroke-2",
              feedback === "dislike" && "stroke-2 text-red-800"
            )}
          />
        }
        text={t.winwingpt.dislike}
        onClick={() => handleFeedback("dislike")}
        aria-pressed={feedback === "dislike"}
      />
    </div>
  );
};

type AnimatedButtonProps = {
  active: boolean;
  icon: React.ReactNode;
  text: string;
  onClick: () => void;
} & ComponentProps<"button">;

const AnimatedButton = ({
  active,
  icon,
  text,
  onClick,
  ...props
}: AnimatedButtonProps) => {
  const [{ x }, api] = useSpring(
    () => ({
      from: { x: 0 },
      config: { duration: 1000 },
    }),
    []
  );

  return (
    <button
      {...props}
      onClick={() => {
        api.start({ from: { x: 0 }, to: { x: 1 } });
        onClick();
      }}
      className={twMerge(
        "flex items-center gap-2 hover:font-medium",
        active && "font-medium"
      )}
    >
      <animated.div
        style={{
          width: "20px",
          height: "20px",
          scale: x.to({
            range: [0, 0.25, 0.35, 0.45, 0.5, 0.55, 0.65, 0.75, 1],
            output: [1, 0.95, 0.9, 0.95, 1, 1.05, 1.1, 1.05, 1],
          }),
        }}
      >
        {icon}
      </animated.div>
      {text}
    </button>
  );
};

export default FeedbackButtons;
